import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TEMPLATE_REFERENCE, ReferenceEntry } from '../data/templateReference';
import { previewTemplate } from '../api/templates';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';

type Tab = 'reference' | 'tester';

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: Tab;
  initialSection?: string;
}

export function TemplateReferenceDrawer({ open, onClose, initialTab = 'reference', initialSection }: Props) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [search, setSearch] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (open && tab === 'reference' && initialSection) {
      const t = setTimeout(() => {
        contentRef.current?.querySelector(`#${initialSection}`)?.scrollIntoView({ block: 'start' });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open, tab, initialSection]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return TEMPLATE_REFERENCE;
    return TEMPLATE_REFERENCE
      .map(section => ({
        ...section,
        entries: section.entries.filter(e =>
          e.title.toLowerCase().includes(needle) || e.syntax.toLowerCase().includes(needle)),
      }))
      .filter(section => section.entries.length > 0);
  }, [search]);

  return (
    <Sheet open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex w-[34rem] max-w-full flex-col gap-0 bg-card p-0 sm:max-w-[34rem]">
        <SheetHeader className="p-4 pb-0">
          <SheetTitle>Template reference</SheetTitle>
          <SheetDescription className="sr-only">Template syntax and a live tester.</SheetDescription>
        </SheetHeader>

        <div className="flex gap-1 px-4 mt-3 border-b border-border">
          {(['reference', 'tester'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                tab === t
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'reference' ? 'Reference' : 'Tester'}
            </button>
          ))}
        </div>

        {tab === 'reference' ? (
          <div ref={contentRef} className="flex-1 overflow-y-auto p-4">
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-input rounded px-3 py-1.5 text-sm bg-background mb-4"
            />
            {filtered.map(section => (
              <section key={section.id} id={section.id} className="mb-6 scroll-mt-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  {section.title}
                </h3>
                <div className="space-y-3">
                  {section.entries.map(entry => (
                    <EntryCard key={entry.id} entry={entry} />
                  ))}
                </div>
              </section>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-muted-foreground">No matches.</p>
            )}
          </div>
        ) : (
          <TesterTab />
        )}
      </SheetContent>
    </Sheet>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text).then(
          () => { setCopied(true); setTimeout(() => setCopied(false), 1200); },
          () => {},
        );
      }}
      className="text-xs text-muted-foreground hover:text-primary shrink-0"
      title="Copy"
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
}

function EntryCard({ entry }: { entry: ReferenceEntry }) {
  return (
    <div id={entry.id} className="border border-border rounded p-3 scroll-mt-2">
      <div className="font-medium text-sm mb-1.5">{entry.title}</div>
      <div className="flex items-start gap-2">
        <code className="flex-1 block bg-muted rounded px-2 py-1 text-xs font-mono text-foreground break-all whitespace-pre-wrap">
          {entry.syntax}
        </code>
        <CopyButton text={entry.syntax} />
      </div>
      {entry.example && (
        <div className="mt-1.5 text-xs text-muted-foreground">
          <span className="text-muted-foreground">Example: </span>
          <code className="font-mono break-all">{entry.example}</code>
        </div>
      )}
      {entry.result && (
        <div className="mt-1 text-xs text-muted-foreground">
          <span className="text-muted-foreground">Result: </span>
          <code className="font-mono break-all text-success">{entry.result}</code>
        </div>
      )}
      {entry.note && (
        <p className="mt-1.5 text-xs text-muted-foreground">{entry.note}</p>
      )}
    </div>
  );
}

function TesterTab() {
  const [template, setTemplate] = useState('{{body.name}}');
  const [body, setBody] = useState('{\n  "name": "World"\n}');
  const [asJson, setAsJson] = useState(false);

  const mutation = useMutation({
    mutationFn: () => previewTemplate({ template, body, asJson }),
  });

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Template</label>
        <textarea
          value={template}
          onChange={e => setTemplate(e.target.value)}
          rows={3}
          className="w-full border border-input rounded px-2 py-1.5 text-xs font-mono bg-background"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Example body (JSON)</label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={6}
          className="w-full border border-input rounded px-2 py-1.5 text-xs font-mono bg-background"
        />
      </div>
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input type="checkbox" checked={asJson} onChange={e => setAsJson(e.target.checked)} />
        Evaluate as JSON (apply type coercion)
      </label>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="bg-primary text-primary-foreground px-4 py-1.5 rounded text-sm hover:bg-primary/90 disabled:opacity-50"
      >
        {mutation.isPending ? 'Evaluating…' : 'Evaluate'}
      </button>

      {mutation.isError && (
        <div className="text-xs text-destructive">Request failed.</div>
      )}
      {mutation.data && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Result</label>
          {mutation.data.error ? (
            <pre className="bg-destructive/10 text-destructive rounded-md px-2 py-1.5 text-xs whitespace-pre-wrap break-all">
              {mutation.data.error}
            </pre>
          ) : (
            <pre className="bg-muted rounded px-2 py-1.5 text-xs font-mono text-foreground whitespace-pre-wrap break-all">
              {mutation.data.resolved}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
